import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n"

// Global neighbor search modal (Cmd+K / header search button)
// Starred neighbors are server-rendered in the modal.
// Search results replace starred when typing, starred reappears when cleared.
export default class extends Controller {
  static targets = [
    "modal",
    "input",
    "results",
    "starred",
    "createLink",
    "createQueryText",
    "command",
    "commandsSection",
  ];
  static values = {
    searchUrl: String,
    debounce: { type: Number, default: 100 },
    superAdmin: { type: Boolean, default: false },
    mac: { type: Boolean, default: false },
  };

  declare modalTarget: HTMLDialogElement;
  declare inputTarget: HTMLInputElement;
  declare resultsTarget: HTMLElement;
  declare starredTarget: HTMLElement;
  declare createLinkTarget: HTMLAnchorElement;
  declare createQueryTextTarget: HTMLElement;
  declare hasCreateLinkTarget: boolean;
  declare commandTargets: HTMLElement[];
  declare commandsSectionTarget: HTMLElement;
  declare hasCommandsSectionTarget: boolean;
  declare searchUrlValue: string;
  declare debounceValue: number;
  declare superAdminValue: boolean;
  declare macValue: boolean;

  private timeout?: ReturnType<typeof setTimeout>;
  private abortController: AbortController | null = null;
  private selectedIndex = -1;
  private actionIndex = 0;
  private resultItems: HTMLElement[] = [];
  private boundHandleGlobalKeydown: (event: KeyboardEvent) => void;
  private boundHandleBeforeCache: () => void;
  private boundPinToViewport: () => void;
  private baseCreatePath: string;

  connect() {
    this.boundHandleGlobalKeydown = this.handleGlobalKeydown.bind(this);
    this.boundHandleBeforeCache = this.handleBeforeCache.bind(this);
    this.boundPinToViewport = this.pinToViewport.bind(this);
    document.addEventListener("keydown", this.boundHandleGlobalKeydown);
    document.addEventListener("turbo:before-cache", this.boundHandleBeforeCache);
    window.visualViewport?.addEventListener("resize", this.boundPinToViewport);
    window.visualViewport?.addEventListener("scroll", this.boundPinToViewport);
    this.baseCreatePath = this.hasCreateLinkTarget
      ? this.createLinkTarget.getAttribute("href") || ""
      : "";
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleGlobalKeydown);
    document.removeEventListener("turbo:before-cache", this.boundHandleBeforeCache);
    window.visualViewport?.removeEventListener("resize", this.boundPinToViewport);
    window.visualViewport?.removeEventListener("scroll", this.boundPinToViewport);
    if (this.timeout) clearTimeout(this.timeout);
    this.abortController?.abort();
  }

  private handleBeforeCache() {
    if (this.modalTarget.open) {
      this.modalTarget.close();
    }
  }

  // iOS Safari pans the visual viewport when the keyboard opens over a scrolled
  // page. A fixed dialog stays with the layout viewport, so its top ends up off
  // screen. Pinning the dialog to the visual viewport keeps the input in view.
  // Pinch-zoom moves the visual viewport too; the dialog stays put for that.
  private pinToViewport() {
    const viewport = window.visualViewport;
    if (!viewport || !this.modalTarget.open) return;

    const zoomed = viewport.scale > 1;
    this.modalTarget.style.top = zoomed ? "" : `${viewport.offsetTop}px`;
    this.modalTarget.style.height = zoomed ? "" : `${viewport.height}px`;
  }

  handleGlobalKeydown(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      this.openModal();
    }

    if (this.modalTarget.open) {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        this.startWorkflowForSelected();
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key === "a" && this.superAdminValue) {
        event.preventDefault();
        this.closeModal();
        window.location.href = "/admin";
        return;
      }

      if (this.macValue && event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        if (event.key === "n") {
          event.preventDefault();
          this.navigateResults(1);
          return;
        }
        if (event.key === "p") {
          event.preventDefault();
          this.navigateResults(-1);
          return;
        }
      }

      switch (event.key) {
        case "Escape":
          event.preventDefault();
          this.closeModal();
          break;
        case "ArrowDown":
          event.preventDefault();
          this.navigateResults(1);
          break;
        case "ArrowUp":
          event.preventDefault();
          this.navigateResults(-1);
          break;
        case "Enter":
          event.preventDefault();
          this.selectResult();
          break;
        case "Tab":
          event.preventDefault();
          this.cycleRowAction(event.shiftKey ? -1 : 1);
          break;
      }
    }
  }

  openModal() {
    this.modalTarget.showModal();
    this.pinToViewport();
    this.inputTarget.focus();
    this.inputTarget.value = "";
    this.selectedIndex = -1;
    this.updateCreateLink("");
    this.filterCommands("");
    this.showStarred();
  }

  closeModal() {
    this.modalTarget.close();
    this.selectedIndex = -1;
    this.abortController?.abort();
  }

  search() {
    const query = this.inputTarget.value.trim();

    this.updateCreateLink(query);
    this.filterCommands(query.toLowerCase());
    this.rebuildResultItems();

    if (this.timeout) clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      if (query.length < 2) {
        this.showStarred();
        return;
      }
      this.performSearch(query);
    }, this.debounceValue);
  }

  private showStarred() {
    this.resultsTarget.classList.add("hidden");
    this.starredTarget.classList.remove("hidden");
    this.rebuildResultItems();
  }

  // Show only commands whose label/keywords contain the query; hide the
  // Actions section when none match.
  private filterCommands(query: string) {
    this.commandTargets.forEach((item) => {
      // The create-neighbor row is the affordance to add the typed name, so it
      // stays visible regardless of query — its keywords never match the name.
      if (this.hasCreateLinkTarget && item.contains(this.createLinkTarget)) {
        item.style.display = "";
        return;
      }

      const keywords =
        item.dataset.commandKeywords || item.textContent?.toLowerCase() || "";
      item.style.display = keywords.includes(query) ? "" : "none";
    });

    if (this.hasCommandsSectionTarget) {
      this.commandsSectionTarget.classList.toggle(
        "hidden",
        this.visibleCommands().length === 0,
      );
    }
  }

  private visibleCommands(): HTMLElement[] {
    return this.commandTargets.filter((item) => item.style.display !== "none");
  }

  // Keyboard navigation spans one merged list: whichever neighbor list
  // (starred or search results) is showing first, then the visible commands.
  private rebuildResultItems() {
    const neighborContainer = this.starredTarget.classList.contains("hidden")
      ? this.resultsTarget
      : this.starredTarget;
    const neighbors = Array.from(
      neighborContainer.querySelectorAll("li"),
    ) as HTMLElement[];

    this.resultItems = [...neighbors, ...this.visibleCommands()];
    this.selectedIndex = this.resultItems.length > 0 ? 0 : -1;
    this.actionIndex = 0;
    this.updateSelection();
  }

  // A row's keyboard-selectable actions: the row link (default), then any
  // per-row workflow launch button. Tab cycles between them.
  private rowActions(item: HTMLElement): HTMLElement[] {
    return [
      item.querySelector("a"),
      ...Array.from(item.querySelectorAll("[data-workflow-launch-button]")),
    ].filter(Boolean) as HTMLElement[];
  }

  private cycleRowAction(direction: number) {
    const item = this.resultItems[this.selectedIndex];
    if (!item) return;

    const count = this.rowActions(item).length;
    if (count < 2) return;

    this.actionIndex = (this.actionIndex + direction + count) % count;
    this.updateSelection();
  }

  private updateCreateLink(query: string) {
    if (!this.hasCreateLinkTarget) return;

    if (query.length > 0) {
      const parts = query.split(/\s+/);
      const url = new URL(this.baseCreatePath, window.location.origin);

      if (parts.length === 1) {
        url.searchParams.set("neighbor[first_name]", parts[0]);
      } else if (parts.length === 2) {
        url.searchParams.set("neighbor[first_name]", parts[0]);
        url.searchParams.set("neighbor[last_name]", parts[1]);
      } else {
        url.searchParams.set("neighbor[first_name]", parts[0]);
        url.searchParams.set("neighbor[middle_name]", parts[1]);
        url.searchParams.set("neighbor[last_name]", parts.slice(2).join(" "));
      }

      this.createLinkTarget.setAttribute("href", url.pathname + url.search);
      this.createQueryTextTarget.textContent = i18n.t('neighbor_index_search.add_named_neighbor', { name: query });
    } else {
      this.createLinkTarget.setAttribute("href", this.baseCreatePath);
      this.createQueryTextTarget.textContent = i18n.t('neighbor_index_search.add_new_neighbor');
    }
  }

  private async performSearch(query: string) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    // Hide starred, show search results
    this.starredTarget.classList.add("hidden");
    this.resultsTarget.classList.remove("hidden");

    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", query);

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    try {
      this.showLoadingState();

      const response = await fetch(url.toString(), {
        headers: {
          Accept: "text/html",
          "X-CSRF-Token": csrfToken || "",
        },
        signal: this.abortController.signal,
      });

      if (response.ok) {
        const html = await response.text();
        this.resultsTarget.innerHTML = html;
        this.setupResultNavigation();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      console.error("Neighbor search failed:", error);
      this.showErrorState();
    }
  }

  private setupResultNavigation() {
    this.rebuildResultItems();

    this.resultsTarget.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", () => {
        this.modalTarget.close();
      });
    });
  }

  private navigateResults(direction: number) {
    if (this.resultItems.length === 0) return;

    this.selectedIndex = Math.max(
      0,
      Math.min(this.resultItems.length - 1, this.selectedIndex + direction),
    );
    this.actionIndex = 0;
    this.updateSelection();
  }

  private updateSelection() {
    this.resultItems.forEach((item, index) => {
      const selected = index === this.selectedIndex;
      const link = item.querySelector("a");
      if (link) {
        link.classList.toggle("bg-gray-100", selected);
      }

      this.rowActions(item).forEach((action, actionIdx) => {
        if (action instanceof HTMLAnchorElement) return; // row highlight covers the default action
        const active = selected && actionIdx === this.actionIndex;
        action.classList.toggle("ring-2", active);
        action.classList.toggle("ring-primary", active);
        action.classList.toggle("bg-primary/10", active);
        action.classList.toggle("text-primary", active);
      });
    });

    if (this.resultItems[this.selectedIndex]) {
      this.resultItems[this.selectedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }

  private selectResult() {
    const item = this.resultItems[this.selectedIndex];
    if (!item) return;

    const action = this.rowActions(item)[this.actionIndex];
    if (!action) return;

    // The launch button closes the modal itself (startWorkflow -> openDrawer).
    if (action instanceof HTMLAnchorElement) this.modalTarget.close();
    action.click();
  }

  // Launch a workflow for the row's neighbor: closes the modal and loads the
  // shared picker into the right drawer (turbo:frame-load->modal#open opens it).
  startWorkflow(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const url = (event.currentTarget as HTMLElement).dataset.launchUrl;
    if (url) this.openDrawer(url);
  }

  // Cmd/Ctrl+Enter: start a workflow for the keyboard-selected neighbor.
  private startWorkflowForSelected() {
    const item = this.resultItems[this.selectedIndex];
    item
      ?.querySelector<HTMLElement>("[data-workflow-launch-button]")
      ?.click();
  }

  private openDrawer(url: string) {
    this.closeModal();
    document.getElementById("drawer_frame")?.setAttribute("src", url);
  }

  private showLoadingState() {
    this.resultsTarget.innerHTML = `
      <div class="text-center py-4">
        <div class="loading loading-spinner loading-md text-gray-500"></div>
        <p class="text-gray-500 mt-1">${i18n.t("neighbor_search.searching")}</p>
      </div>
    `;
  }

  private showErrorState() {
    this.resultsTarget.innerHTML = `
      <div class="text-center py-8 text-gray-500">
        <svg class="w-12 h-12 mx-auto mb-4 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p>${i18n.t("neighbor_search.something_went_wrong")}</p>
      </div>
    `;

    this.resultItems = [];
    this.selectedIndex = -1;
  }
}
